import { notEmpty } from "@product/scmp-sdk";
import { useAtomValue } from "jotai";
import type { PageProps } from "next";
import { useEffect, useMemo } from "react";
import { graphql, usePreloadedQuery } from "react-relay";
import type { RelayComponent } from "relay-nextjs";
import { withRelay } from "relay-nextjs";

import { AppFooter } from "scmp-app/components/app-footer";
import { FallbackLoading } from "scmp-app/components/fallback-loading";
import { PostiesPaywallContainer } from "scmp-app/components/posties/paywall-container";
import { PostiesSeo } from "scmp-app/components/posties/posties-seo";
import { PostiesVideoLandingVideo } from "scmp-app/components/posties/video/landing-video";
import { PostiesVideoCard } from "scmp-app/components/posties/video/more-videos/posties-video-card";
import { VideoPostiesSeo } from "scmp-app/components/posties/video/video-posties-seo";
import { trackingAtom } from "scmp-app/components/tracking";
import { createClientEnvironment } from "scmp-app/lib/relay/environment.client";
import { parseQueryString } from "scmp-app/lib/utils";
import type { videoPostiesPageQuery } from "scmp-app/queries/__generated__/videoPostiesPageQuery.graphql";

import { usePostiesVideoList } from "./hooks";
import {
  Container,
  LandingVideoContainer,
  LoadingText,
  MainHeading,
  MoreVideosCardsAndLoadingContainer,
  MoreVideosCardsContainer,
  MoreVideosContainer,
  MoreVideosTitle,
} from "./styles";

const videoPageSize = 8;

const query = graphql`
  query videoPostiesPageQuery(
    $after: String
    $count: Int = 9
    $hasRouteVideo: Boolean!
    $routeVideoEntityId: String
  ) {
    videoFromRoute: content(contentType: VIDEO, filter: { entityId: $routeVideoEntityId })
      @include(if: $hasRouteVideo) {
      ... on Video {
        ...landingVideoVideo
        ...videoPostiesSeoVideo
      }
    }
    ...hooksPostiesVideoVideo @arguments(after: $after, count: $count)
  }
`;

const PostiesVideo: RelayComponent<unknown, videoPostiesPageQuery> = ({ preloadedQuery }) => {
  const { trackPageView } = useAtomValue(trackingAtom);

  useEffect(() => {
    trackPageView?.();
  }, [trackPageView]);

  const data = usePreloadedQuery(query, preloadedQuery);
  const { hasNextPage, infiniteScrollTriggerReference, videos } = usePostiesVideoList(
    data,
    videoPageSize,
  );

  const [firstFromList, ...restFromList] = videos;
  const mainVideo = useMemo(
    () => data.videoFromRoute ?? firstFromList,
    [data.videoFromRoute, firstFromList],
  );

  const renderSeo = () =>
    notEmpty(data.videoFromRoute) ? (
      <VideoPostiesSeo reference={data.videoFromRoute} />
    ) : (
      <PostiesSeo />
    );

  const moreVideoList = notEmpty(data.videoFromRoute) ? videos : restFromList;

  if (videos.length === 0) return null;
  return (
    <>
      {renderSeo()}
      <Container>
        <LandingVideoContainer>
          <MainHeading>VIDEO</MainHeading>
          {mainVideo && <PostiesVideoLandingVideo reference={mainVideo} />}
        </LandingVideoContainer>
        <MoreVideosContainer>
          <MoreVideosTitle>More</MoreVideosTitle>
          <MoreVideosCardsAndLoadingContainer>
            <MoreVideosCardsContainer>
              {moreVideoList?.map((video, index) => (
                <PostiesVideoCard key={index} reference={video} />
              ))}
            </MoreVideosCardsContainer>
            {hasNextPage && (
              <LoadingText ref={element => infiniteScrollTriggerReference(element)}>
                Loading...
              </LoadingText>
            )}
          </MoreVideosCardsAndLoadingContainer>
        </MoreVideosContainer>
        <PostiesPaywallContainer />
        {!hasNextPage && <AppFooter variant="posties" />}
      </Container>
    </>
  );
};

PostiesVideo.displayName = "PostiesVideo";

const pageProps: PageProps = {
  appBarConfiguration: {
    variant: "posties/generic",
  },
};

export default withRelay(PostiesVideo, query, {
  clientSideProps: () => pageProps,
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("scmp-app/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: () => Promise.resolve(pageProps),
  variablesFromContext: context => {
    const routeVideoEntityId = parseQueryString(context.query.entityId) ?? "";
    return {
      hasRouteVideo: notEmpty(routeVideoEntityId),
      routeVideoEntityId,
    };
  },
});
